import { store } from '@/store';
import { fetchCallStream, type FetchCallStreamOptions } from '@/store/api/call';
import {
  setCallStreamInit,
  setCallStreamLoading,
  clearCallStreamLoading,
  appendTranscriptChunk,
  clearCallContext,
  removeContactSession,
  setSessionCaseInfo,
} from '@/store/slices/callSlice';
import {
  clearCustomerInfo,
  fetchCustomerForCall,
  setCustomerInfo,
} from '@/store/slices/customerSlice';
import {
  clearCaseInfo,
  fetchCaseById,
  setCaseInfo,
} from '@/store/slices/caseSlice';
import type { CallStreamEvent } from '@/types/call';
import { resolveContactWorkSpaceID } from '@/utils/contactWorkspace';

let streamAbortController: AbortController | null = null;
let streamContactId: string | null = null;

function parseSSELine(line: string): CallStreamEvent | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('data: ')) return null;
  try {
    return JSON.parse(trimmed.slice(6)) as CallStreamEvent;
  } catch {
    return null;
  }
}

function clearCaseForContact(contactId: string): void {
  store.dispatch(setSessionCaseInfo({ contactId, caseInfo: null }));
  if (store.getState().call.focusedContactId === contactId) {
    store.dispatch(clearCaseInfo());
  }
}

/**
 * CallService: opens GET /call/stream (SSE) when the agent accepts a contact,
 * dispatches init, transcript, and stream_end events to the store.
 */
export const callService = {
  /**
   * Open call stream for the given contact. Fetches initial state via SSE init,
   * then processes transcript and stream_end events.
   */
  async initiateCall(
    contactId: string,
    options?: FetchCallStreamOptions
  ): Promise<void> {
    // Idempotency guard: the contact machine emits initiateStream on
    // 'connected' as a fallback for inbound voice, and warm-consult streams
    // must not be clobbered by a plain re-init. Skip when a stream for this
    // contact is already loading or open.
    const session = store.getState().call.sessions[contactId];
    if (session?.isStreamInitializing || session?.streamActive) {
      console.log('[callService] stream already active — skipping re-init', {
        contactId,
      });
      return;
    }
    streamAbortController?.abort();
    streamAbortController = new AbortController();
    streamContactId = contactId;
    store.dispatch(
      setCallStreamLoading({ contactId, type: 'voice', startedAt: Date.now() })
    );
    console.log('[callService] opening call stream', { contactId, options });

    try {
      const response = await fetchCallStream(
        contactId,
        streamAbortController.signal,
        options
      );

      if (!response.ok) {
        store.dispatch(clearCallStreamLoading({ contactId }));
        console.error('[callService] Call stream error:', {
          status: response.status,
          body: await response.text(),
          contactId,
          options,
        });
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        store.dispatch(clearCallStreamLoading({ contactId }));
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let streamEnded = false;

      while (!streamEnded) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE events are separated by double newline
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() ?? '';

        for (const block of blocks) {
          const dataLine = block.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          const event = parseSSELine(dataLine);
          if (!event) continue;
          switch (event.type) {
            case 'init': {
              // caseId is optional; outbound calls do not have a linked case.
              // Direct-connect calls can include a backend-created dummy case.
              const isDirectConnect = Boolean(event.isDirectConnect);
              const callerPhoneNumber = event.callerPhoneNumber ?? '';
              const clientAlert = event.clientAlert;
              const authenticated = event.authenticated;
              const isCallback = event.isCallback;
              const intentName = event.intentName ?? '';
              const workSpaceID = resolveContactWorkSpaceID(
                event.workspaceId,
                authenticated
              );
              const caseId = event.caseId ?? null;
              const previousMessages = isDirectConnect
                ? []
                : (event.previousMessages ?? []);
              store.dispatch(
                setCallStreamInit({
                  contactId,
                  type: 'voice',
                  previousMessages,
                  callerPhoneNumber,
                  callerName: event.callerName ?? '',
                  clientAlert,
                  authenticated,
                  isCallback,
                  intentName,
                  bufferedTranscripts: event.bufferedTranscripts ?? [],
                  recordingState: event.recordingState,
                  isDirectConnect,
                  caseHistory: event.caseHistory ?? [],
                  casePrefill: event.casePrefill ?? null,
                })
              );
              if (workSpaceID) {
                store.dispatch(
                  fetchCustomerForCall({
                    workSpaceID,
                    contactId,
                  })
                );
              }
              if (caseId) {
                store.dispatch(fetchCaseById({ caseId, contactId }));
              } else {
                clearCaseForContact(contactId);
              }
              break;
            }
            case 'transcript':
              store.dispatch(
                appendTranscriptChunk({
                  contactId,
                  transcript: event.transcript,
                  isPartial: event.isPartial,
                  timestamp: event.timestamp,
                  resultId: event.resultId,
                  startTime: event.startTime,
                  endTime: event.endTime,
                  sentenceId: event.sentenceId,
                  speaker: event.speaker,
                  speakerObject: event.speakerObject,
                })
              );
              break;
            case 'ping':
              // Keep-alive messages are intentionally ignored by consumers.
              break;
            case 'stream_end':
              streamEnded = true;
              break;
          }
          if (streamEnded) break;
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Expected when clearCall() aborts
        return;
      }
      store.dispatch(clearCallStreamLoading({ contactId }));
      console.error('[callService] Stream error:', err);
    } finally {
      streamAbortController = null;
      streamContactId = null;
    }
  },

  /**
   * Clear call context and abort the stream if open.
   */
  clearCall(): void {
    if (streamAbortController) {
      streamAbortController.abort();
      streamAbortController = null;
    }
    store.dispatch(clearCallContext());
    store.dispatch(clearCustomerInfo());
    store.dispatch(clearCaseInfo());
  },

  /**
   * Remove one contact session without clearing other active call/chat sessions.
   */
  clearContact(contactId: string): void {
    if (streamContactId === contactId && streamAbortController) {
      streamAbortController.abort();
      streamAbortController = null;
      streamContactId = null;
    }
    store.dispatch(removeContactSession({ contactId }));
    // Sync the global customer & case slices to match the newly focused session
    // (if another session took focus), or clear them (if no sessions remain).
    // Without this, stale data from the removed session persists in CustomerInfoCard / CaseCard.
    const state = store.getState();
    const newFocusedId = state.call.focusedContactId;
    if (newFocusedId) {
      const newSession = state.call.sessions[newFocusedId];
      store.dispatch(setCustomerInfo(newSession?.currentCustomer ?? null));
      store.dispatch(setCaseInfo(newSession?.currentCase ?? null));
    } else {
      store.dispatch(clearCustomerInfo());
      store.dispatch(clearCaseInfo());
    }
  },
};
